import { Box, Divider } from '@mui/material'
import Button from '~/src/components/mui-wrappers/buttons/Button'
import { useCustomMuiTheme } from '~/src/hooks/useCustomMuiTheme'
import { useTranslations } from '~/src/hooks/useTranslations'
import { FormikProps } from 'formik'
import useDialog from '~/src/hooks/useDialog'
import SocialLogins from '~/src/components/auth/social-logins/SocialLogins'
import { LoginTypeSelectionFormInterface } from '~/src/types/forms/LoginTypeSelectionFormInterface'
import useAuthenticationFlowDialogs from '~/src/hooks/auth/flow/useAuthenticationFlowDialogs'
import { useEoState, useEoValue } from '~/src/hooks/useEoState'
import AUTHENTICATION_FLOW_STATE, {
  AuthenticationFlowState
} from '~/src/stores/authentication-flow'
import { AuthenticationFlowDialogNameEnum } from '~/src/types/DialogNameEnum'
import Params from '~/src/router/enums/params.enum'
import { useLocation } from 'react-router-dom'
import { useRouterPush } from '~/src/hooks/router/useRouterPush'
import { FIRM_SELECTOR } from '~/src/stores/firm'
import useGetQueryParam from '~/src/hooks/router/useGetQueryParam'
import ProfileRoutesEnum from '~/src/router/enums/profile.enum'
import { useEffect, useState } from 'react'
import { SocialLoginType } from '@eo-storefronts/eo-core'
import { GuestMissingFieldsDialogProps } from '~/src/components/auth/dialogs/missing-fields/GuestMissingFieldsDialog'

export type LoginTypeSelectionFormActionTypes = 'password' | 'guest' | SocialLoginType

interface Props {
  form: FormikProps<LoginTypeSelectionFormInterface>,
}

const LoginTypeSelectionFormActions = ({ form }: Props) => {
  const { params: redirectTo } = useGetQueryParam(Params.REDIRECT_TO)
  const { pathname } = useLocation()
  const { push } = useRouterPush()
  const { goToStep } = useAuthenticationFlowDialogs()
  const firm = useEoValue(FIRM_SELECTOR)
  const [ authenticationFlowState, setAuthenticationFlowState ] = useEoState<AuthenticationFlowState>(AUTHENTICATION_FLOW_STATE)
  const { t } = useTranslations()
  const theme = useCustomMuiTheme()
  const { dismissAll } = useDialog()
  const [
    loginTypeSelectionFormActionClicked,
    setLoginTypeSelectionFormActionClicked
  ] = useState<LoginTypeSelectionFormActionTypes | null>(null)

  const _handleOnContinueAsGuestClick = () => {
    setLoginTypeSelectionFormActionClicked('guest')
    if (redirectTo && Object.keys(ProfileRoutesEnum).includes(redirectTo)) {
      push(pathname)
    }

    dismissAll()
    goToStep<GuestMissingFieldsDialogProps>(
      AuthenticationFlowDialogNameEnum.AUTHENTICATION_FLOW_GUEST_MISSING_FIELDS,
      {
        showBackButton: true
      }
    )
  }

  const setContinueWithMethod = () => {
    setAuthenticationFlowState((state: AuthenticationFlowState) => ({
      ...state,
      loginType: state.loginType === 'email' ? 'phone' : 'email'
    }))
    void form.resetForm()
  }

  useEffect(() => {
    if (authenticationFlowState.isLoading) {
      return
    }

    /**
     * reset the buttons state
     */
    setLoginTypeSelectionFormActionClicked(null)
  }, [ authenticationFlowState.isLoading ])

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateAreas: '"button" "divider" "continueWith" "social-logins" "links"',
        gridTemplateColumns: '1fr',
        gridTemplateRows: 'max-content',
        gap: 2,
        [theme.breakpoints.down('sm')]: {
          gridTemplateColumns: '1fr',
          gridTemplateRows: 'repeat(4, max-content)',
          gridTemplateAreas: '"button"' +
            '"divider"' +
            '"continueWith"' +
            '"social-logins"' +
            '"links"',
          gap: 1
        }
      }}
    >
      <Button
        disabled={!form.isValid || authenticationFlowState.isLoading}
        loading={authenticationFlowState.isLoading && loginTypeSelectionFormActionClicked === 'password'}
        fullWidth
        variant='contained'
        type='submit'
        onClick={() => setLoginTypeSelectionFormActionClicked('password')}
        sx={{
          gridArea: 'button',
          fontWeight: 'bold',
          fontSize: '1rem',
          minHeight: '42px',
          textTransform: 'none'
        }}
      >
        {t('global.continue')}
      </Button>

      <Divider sx={{
        gridArea: 'divider',
        py: 1
      }}>
        {t('global.or').toLowerCase()}
      </Divider>

      <Button
        disabled={authenticationFlowState.isLoading}
        fullWidth
        variant='contained'
        onClick={setContinueWithMethod}
        sx={{
          gridArea: 'continueWith',
          fontWeight: 'bold',
          fontSize: '1rem',
          minHeight: '42px',
          textTransform: 'none'
        }}
      >
        {authenticationFlowState.loginType === 'email'
          ? t('auth.loginTypeSelection.dialog.actions.continueWithPhoneNumber')
          : t('auth.loginTypeSelection.dialog.actions.continueWithEmail')}
      </Button>

      <Box gridArea='social-logins'>
        <SocialLogins
          isLoading={authenticationFlowState.isLoading}
          loginTypeSelectionFormActionClicked={loginTypeSelectionFormActionClicked}
          onClick={(type: SocialLoginType) => setLoginTypeSelectionFormActionClicked(type)}
        />
      </Box>

      <Box gridArea='links'>
        {firm?.settings.theme.flow?.ordering.allowGuestCheckout && (
          <Button
            fullWidth
            loading={authenticationFlowState.isLoading && loginTypeSelectionFormActionClicked === 'guest'}
            disabled={authenticationFlowState.isLoading}
            variant={authenticationFlowState.isLoading ? 'contained' : 'text'}
            onClick={_handleOnContinueAsGuestClick}
            sx={{
              fontSize: '1rem',
              minHeight: '42px',
              textTransform: 'none'
            }}>
            {t('global.continueAsGuest')}
          </Button>
        )}
      </Box>
    </Box>
  )
}

export default LoginTypeSelectionFormActions
